import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

/**
 * Cheap OpenSearch-backed count for the MT1-1393 "Import items" dialog.
 * Once the user picks a source project + source room, we ask the backend
 * how many items would be pulled before they commit — a basic "is this
 * the room I meant?" sanity signal. The number is the upper bound;
 * individual items can still fail target-side validation during the
 * actual copy.
 *
 * Rooms are nested under buildings in the URL space, not directly under
 * projects — first cut hit /projects/{projectId}/rooms/{roomId}/... and
 * 404'd at the gateway because that path doesn't exist.
 */
export const getCopyableItemCount = async (
  orgId: string,
  sourceProjectId: string,
  sourceBuildingId: string,
  sourceRoomId: string,
): Promise<{ count: number }> => {
  const { data } = await api.get<unknown, ApiResponse<{ count: number }>>(
    `/orgs/${orgId}/projects/${sourceProjectId}/buildings/${sourceBuildingId}/rooms/${sourceRoomId}/copyable-item-count`,
  );
  return data;
};

export const getCopyableItemCountQueryKey = (
  orgId?: string,
  sourceProjectId?: string,
  sourceBuildingId?: string,
  sourceRoomId?: string,
) => [
  'org',
  orgId,
  'project',
  sourceProjectId,
  'building',
  sourceBuildingId,
  'room',
  sourceRoomId,
  'copyable-item-count',
];

export const getCopyableItemCountQueryOptions = (
  orgId?: string,
  sourceProjectId?: string,
  sourceBuildingId?: string,
  sourceRoomId?: string,
) =>
  queryOptions({
    queryKey: getCopyableItemCountQueryKey(orgId, sourceProjectId, sourceBuildingId, sourceRoomId),
    queryFn: () =>
      getCopyableItemCount(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        orgId!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        sourceProjectId!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        sourceBuildingId!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        sourceRoomId!,
      ),
    enabled: !!orgId && !!sourceProjectId && !!sourceBuildingId && !!sourceRoomId,
  });

interface UseCopyableItemCountOptions {
  orgId: string | undefined;
  sourceProjectId: string | undefined;
  sourceBuildingId: string | undefined;
  sourceRoomId: string | undefined;
}

export const useCopyableItemCount = ({
  orgId,
  sourceProjectId,
  sourceBuildingId,
  sourceRoomId,
}: UseCopyableItemCountOptions) =>
  useQuery(
    getCopyableItemCountQueryOptions(orgId, sourceProjectId, sourceBuildingId, sourceRoomId),
  );
